<aside class="filter-panel" role="dialog" aria-label="Table filters">
  <header class="filter-panel__header">
    <div>
      <div class="filter-panel__eyebrow">RMS table tools</div>
      <h2>Filters</h2>
      <p>Choose fields, set conditions, or apply a saved preset.</p>
    </div>

    <button type="button"
            mat-icon-button
            class="filter-panel__close"
            aria-label="Close filter panel"
            (click)="closePanel()">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <section class="filter-panel__toolbar">
    <button type="button"
            mat-stroked-button
            class="preset-button"
            [matMenuTriggerFor]="presetMenu"
            [disabled]="loadingPresets">
      <mat-icon>tune</mat-icon>
      <span>{{ SelectedFilter?.FilterPresetName || 'Preset filters' }}</span>
      <mat-icon>expand_more</mat-icon>
    </button>

    <mat-menu #presetMenu="matMenu" class="rms-filter-menu">
      <div class="preset-menu__heading">Default</div>
      @if (DefaultFilter) {
      <button mat-menu-item (click)="SelectPreset(DefaultFilter)">
        <mat-icon>star</mat-icon>
        <span>{{ DefaultFilter.FilterPresetName }}</span>
      </button>
      } @else {
      <div class="preset-menu__empty">No default preset</div>
      }

      <div class="preset-menu__heading">My filters</div>
      @for (preset of CustomFilters; track preset.FilterPresetID) {
      <div class="my-filter-row">
      <button mat-menu-item (click)="SelectPreset(preset)">
        <mat-icon>person</mat-icon>
        <span>{{ preset.FilterPresetName }}</span>
      </button>
      <button type="button" mat-icon-button
              [attr.aria-label]="'Delete saved filter ' + preset.FilterPresetName"
              title="Delete saved filter" [disabled]="savingPreset"
              (click)="DeletePreset(preset, $event)">
        <mat-icon>delete_outline</mat-icon>
      </button>
      </div>
      } @empty {
      <div class="preset-menu__empty">No custom presets</div>
      }

      <div class="preset-menu__heading">System filters</div>
      @for (preset of SystemDefaultFilters; track preset.FilterPresetID) {
      <button mat-menu-item (click)="SelectPreset(preset)">
        <mat-icon>verified</mat-icon>
        <span>{{ preset.FilterPresetName }}</span>
      </button>
      } @empty {
      <div class="preset-menu__empty">No system presets</div>
      }
    </mat-menu>

    <div class="filter-panel__actions">
      <button type="button" mat-button (click)="clearFilters()">
        <mat-icon>filter_alt_off</mat-icon>
        Clear
      </button>

      <button type="button" mat-button [disabled]="savingPreset" (click)="SaveFilter()">
        <mat-icon>save_as</mat-icon>
        Save as new
      </button>
      @if (
      SelectedFilter;
      as selectedFilter
      ) {
      @if (
      selectedFilter.FilterPresetID &&
      !selectedFilter.SystemCreated
      ) {
      <button type="button"
              mat-flat-button
              class="primary-action"
              [disabled]="savingPreset"
              (click)="UpdateFilter()">
        <mat-icon>save</mat-icon>
        Update
      </button>
      }
      }
    </div>
  </section>

  @if (SelectedFilter; as selectedFilter) {
  <section class="selected-preset">
    <div>
      <span>Selected preset</span>
      <strong>{{ selectedFilter.FilterPresetName }}</strong>
    </div>

    @if (!selectedFilter.SystemCreated) {
    <rms-checkbox label="Default"
                  [(ngModel)]="selectedFilter.DefaultFilter"
                  (ngModelChange)="UpdateDefault()">
    </rms-checkbox>
    }
  </section>
  }

  <section class="filter-panel__content">
    <div class="filter-list-heading">
      <div>
        <h3>Filter by</h3>
        <p>Select all fields that should apply. Active fields use AND logic.</p>
      </div>
      @if (filters$ | async; as filters) {
      <span class="active-count">
        {{ (filters | keyvalue).length }} active
      </span>
      } @else {
      <span class="active-count">
        0 active
      </span>
      }
    </div>

    <div class="filter-list">
      @for (column of columns; track trackColumn($index, column)) {
      <article class="filter-card" [class.filter-card--active]="column.selected">
        <div class="filter-card__title">
          <rms-checkbox [(ngModel)]="column.selected"
                        (ngModelChange)="updateFilters(column, true)">
          </rms-checkbox>

          <button type="button" class="filter-card__toggle" (click)="toggleColumn(column)">
            <span>{{ column.label }}</span>
            <mat-icon>{{ column.selected ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </div>

        @if ((filters$ | async)?.[column.key || column.labelKey || '']) {
        <div class="filter-card__body">
          <div class="filter-control filter-control--type">
            @let filter = getFilterValue(column);
            @let conditionOptions = getSelectOptions(column);
            <rms-select [options]="conditionOptions"
                        label="Condition"
                        labelKey="label"
                        valueKey="value"
                        [ngModel]="filter.FilterType"
                        (ngModelChange)="UpdateFilterType(column, $event)">
            </rms-select>
          </div>

          @if (getFilterValue(column).FilterType !== 'Empty' &&
          getFilterValue(column).FilterType !== 'Not Empty') {
          <div class="filter-control filter-control--value">
            @switch (getFilterType(column)) {
            @case ('select') {
            <rms-select [options]="column.dropdownSearchOptions || []"
                        label="Value"
                        labelKey="label"
                        valueKey="value"
                        [ngModel]="getFilterValue(column).value"
                        (ngModelChange)="UpdateFilterValue(column, $event)">
            </rms-select>
            }
            @case ('number') {
            @if (getFilterValue(column).FilterType === 'Between') {
            <div class="range-grid">
              <rms-number label="From"
                          [ngModel]="getFilterValue(column).value?.Start"
                          (ngModelChange)="UpdateFilterValue(column, { valueType: 'Start', value: $event })">
              </rms-number>
              <rms-number label="To"
                          [ngModel]="getFilterValue(column).value?.End"
                          (ngModelChange)="UpdateFilterValue(column, { valueType: 'End', value: $event })">
              </rms-number>
            </div>
            } @else {
            <rms-number label="Value"
                        [ngModel]="getFilterValue(column).value"
                        (ngModelChange)="UpdateFilterValue(column, $event)">
            </rms-number>
            }
            }

            @case ('date') {
            @if (getFilterValue(column).FilterType === 'Between') {
            <div class="range-grid">
              <rms-date label="Start"
                        [ngModel]="getFilterValue(column).value?.StartDate"
                        (ngModelChange)="UpdateFilterValue(column, { valueType: 'StartDate', value: $event })">
              </rms-date>
              <rms-date label="End"
                        [ngModel]="getFilterValue(column).value?.EndDate"
                        (ngModelChange)="UpdateFilterValue(column, { valueType: 'EndDate', value: $event })">
              </rms-date>
            </div>
            } @else {
            <rms-date label="Date"
                      [ngModel]="getFilterValue(column).value"
                      (ngModelChange)="UpdateFilterValue(column, $event)">
            </rms-date>
            }
            }

            @case ('checkbox') {
            <div class="boolean-value">
              {{ getFilterValue(column).FilterType === '=' ? 'Show checked rows' : 'Show unchecked rows' }}
            </div>
            }

            @default {
            <rms-input type="text"
                       label="Value"
                       [ngModel]="getFilterValue(column).value"
                       (ngModelChange)="UpdateFilterValue(column, $event)">
            </rms-input>
            }
            }
          </div>
          } @else {
          <div class="empty-condition-note">
            <mat-icon>info</mat-icon>
            No value is required for this condition.
          </div>
          }
        </div>
        }
      </article>
      }
    </div>
  </section>
</aside>
